import React, { useCallback, useMemo, useState } from 'react'
import { Alert, Box, Button, Typography } from '@mui/material'
import BarChartIcon from '@mui/icons-material/BarChart'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'

import PageHeader from '../../components/system/PageHeader'
import { BRAND_GRADIENT } from '../../components/social-media/design-tokens'
import { countActiveFilters } from '../../components/social-media/entityQuery'
import UncodedDisclosureButton from '../../components/social-media/UncodedDisclosureButton'
import {
  FilterSidebar,
  LabelFilterBuilder,
  MetadataFilterSection,
  PlatformFilterBar,
} from '../../components/social-media'
import {
  SKY_SIDEBAR_LABEL_KEYS,
  SKY_SIDEBAR_METADATA_KEYS,
} from '../../components/social-media/platformFilter'
import useEntityFilterSummary from '../../hooks/useEntityFilterSummary'
import { useSocialFilters, type UseSocialFiltersResult } from '../../hooks/useSocialFilters'
import {
  CollapsibleSection,
  CommentStream,
  PlaceholderPanel,
  SectionHeader,
  ThemeBreakdown,
  TALL_CHART_HEIGHT,
  UpdatingFade,
  updatingProps,
  updatingSx,
} from '../../components/socialdash'
import SkyLoadProgress from './SkyLoadProgress'
import {
  useSkyCharts,
  useSkyCommentCount,
  useSkyComments,
  useSkyCulturalRelevance,
  useSkyEntityMetrics,
  useSkyOutputOpinionShare,
  useSkyPlatformCounts,
  useSkyProducts,
  useSkySourceCounts,
  useSkyUncodedDisclosure,
  sourceIsSelectable,
} from '../../hooks/socialdash'
import useRouter from '../../hooks/useRouter'
import { SKY_SOCIAL_CUSTOMER, SKY_SOCIAL_JOB, TOKEN_SPEND_VISIBLE } from '../../constants/skySocialMedia'
import { describeAddedCodes, useFrameCodeHistory } from '../../hooks/socialdash/useFrameCodeHistory'
import ProductTabs, { spendLandingProduct } from './ProductTabs'
import { keyMetricsCaption } from './keyMetricsCaption'
import { defaultSkyDateRange } from './defaultDateRange'
import {
  ATTRIBUTION_CHARTS,
  OPINION_SHARE_CHART,
  OUTPUT_TAG_KEYS,
  OVERVIEW_CHARTS,
  STANDARD_CHARTS,
  opinionsOverTimeChart,
  timeSeriesCharts,
} from './charts'
import { CoverageTile, DefaultKpiRow2, EntityMetricTiles, type KpiRow2Context } from './kpiTiles'
import OutputShareCell from './OutputShareCell'
import type { PlatinumData } from '../../types/carbon/models/PlatinumData'

export interface StandardProductDashboardProps {
  /** The manifest slug — what the route segment carries. */
  product: string
  /** Display name for the header, e.g. "Entertainment". */
  title: string
  /** One line under the header describing what the department covers. */
  subtitle?: string
  /**
   * Extra sidebar controls, rendered ABOVE the standard two.
   *
   * ⚠ THIS IS THE SLOT THAT KEEPS THE ADMIN PAGE OUT OF THIS FILE, and it is a
   * render prop rather than a plain node so the caller can reach the filter
   * state without this component learning what the caller renders. §4 forbids a
   * `variant` prop or a per-product conditional here: the admin-only controls
   * are absent by COMPOSITION — a different page passes them — never by a flag.
   * The moment this file branches on WHICH product it is showing, five
   * departments' opinions are entangled in one file again.
   */
  sidebarExtra?: (filters: UseSocialFiltersResult) => React.ReactNode
  /**
   * Whether this page draws the standard campaign picker in the filter bar
   * (#1199). Default TRUE — a department that forgot to opt in would silently
   * ship without it, which is exactly how the platform bar and the comment-date
   * picker each went missing for a week.
   *
   * 🔴 ONE CAMPAIGN CONTROL PER PAGE, AND THIS IS HOW A PAGE DECLINES THE
   * STANDARD ONE. The admin view supplies its own — a tree grouped by
   * department, through `sidebarExtra` — which keeps a raw selection of its own
   * alongside the collapsed value it puts on the wire. A second control reading
   * `filters.campaigns` would show that collapsed value beside the tree's ticks
   * and silently disagree with it: the same two-controls-one-key failure
   * MetadataFilterSection already refuses `source` to avoid.
   *
   * This is NOT the `variant` prop §4 forbids: it says what this page RENDERS,
   * not which product it is showing. The file still never learns that.
   */
  campaignFilter?: boolean
  /**
   * Row 2 of the grid. Defaults to POSITIVE SENTIMENT | RELEVANT SENTIMENT |
   * ATTRIBUTED SENTIMENT | ENGAGEMENT RATE + MEANINGFUL ER (`DefaultKpiRow2`).
   *
   * Like `sidebarExtra`, this is a slot a department fills from its own file,
   * not a flag this file branches on: Brand passes its own row 2 and this file
   * still never learns it is showing Brand (§4).
   */
  kpiRow2?: (ctx: KpiRow2Context) => React.ReactNode
}

const renderDefaultKpiRow2 = (ctx: KpiRow2Context) => <DefaultKpiRow2 {...ctx} />

/**
 * Month trend charts: rows 4 and 5 of the grid, and the Yes-share trends of
 * Sky Relevant and Sky Attributed, which sit in Breakdowns. Row 5 is
 * sentiment WITHIN the relevant / attributed comments (#1489).
 */
const TREND_CHARTS = timeSeriesCharts('month')
const trendCharts = (ids: string[]) => TREND_CHARTS.filter((c) => ids.includes(c.id))
const ROW4_CHARTS = trendCharts(['sentiment-month', 'emotion-month'])
const ROW5_CHARTS = trendCharts(['sentiment-relevant-month', 'sentiment-attributed-month'])
const ATTRIBUTION_TREND_CHARTS = trendCharts(['skyrelevant-month', 'skyattributed-month'])
const OPINIONS_OVER_TIME_CHART = opinionsOverTimeChart('month')

/** True when a chart came back as a table with at least one row. */
const hasRows = (table: PlatinumData | null | undefined): boolean => (table?.side?.vecs?.length ?? 0) > 0

/**
 * The KPI rows (1 and 2). `gap: 0` because every kit tile draws its own right
 * and bottom hairline, the same convention as the old StatStrip.
 */
const KPI_ROW_SX = {
  display: 'grid',
  gap: 0,
  // 1 column on a phone, 2 on a tablet, and on a wide screen one equal column
  // per tile: row 2's standard arrangement is five tiles (its last slot holds
  // Engagement Rate and Meaningful ER side by side), Brand's is four.
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: 'none' },
  gridAutoFlow: { xs: 'row', lg: 'column' },
  gridAutoColumns: { lg: 'minmax(0, 1fr)' },
} as const

/** Two wide cells (row 3), one column on a phone. */
const WIDE_ROW_SX = {
  display: 'grid',
  gap: 0,
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
} as const

/**
 * The arrangement all five departments start with.
 *
 * WHY A SHARED ARRANGEMENT AND NOT FIVE COPIES. §5c's test is that a divergent
 * dashboard costs an afternoon, and five byte-identical 150-line files would
 * fail a different test — every fix applied five times, four of them forgotten.
 * So the five pages are thin, and this is what they are thin ABOUT.
 *
 * HOW A DEPARTMENT DIVERGES. It stops calling this. `SportsDashboard.tsx`
 * becomes its own arrangement of the same kit components — that is the fork, and
 * it costs an afternoon precisely because the primitives, the hooks and the
 * chart definitions are all already there and none of them need touching. What
 * must NOT happen is this component growing a `variant` prop or a per-product
 * conditional: the moment it does, five departments' opinions are entangled in
 * one file again and the fork gets expensive, which is the failure this whole
 * structure exists to prevent.
 *
 * It contains no query logic (the hooks own that) and no chart internals (the
 * kit owns that). What is left is arrangement and copy, which is the definition
 * of a page here.
 */
export const StandardProductDashboard: React.FC<StandardProductDashboardProps> = ({
  product,
  title,
  subtitle,
  sidebarExtra,
  campaignFilter = true,
  kpiRow2 = renderDefaultKpiRow2,
}) => {
  const router = useRouter()
  const [breakdownsExpanded, setBreakdownsExpanded] = useState(false)
  const [commentsExpanded, setCommentsExpanded] = useState(false)
  // Which post tag the OUTPUT OPINION SHARE pie groups by. Sky has not said
  // which one they mean, so the reader chooses; the first is the default.
  const [outputTagKey, setOutputTagKey] = useState(OUTPUT_TAG_KEYS[0].key)

  // The department's real platforms AND its real campaigns, from the
  // product-scoped filter summary. Shared with the sidebar through the
  // react-query cache, so this is one request rather than two.
  //
  // 🔴 THE CAMPAIGN OPTIONS MUST COME FROM HERE AND NOWHERE ELSE (#1199). This
  // is a bodyless GET, so the server scopes it through the ProductScope context
  // local rather than the body rewrite (route_scope.go) — which means the list
  // it returns is exactly this department's campaigns, with this department's
  // counts. Offering anything wider would offer a campaign the server then
  // refuses with a 400, because a selection made entirely of out-of-scope ids is
  // a refusal rather than a silent widening (scopeBodyForProduct).
  const { platforms: availablePlatformCounts, campaigns: availableCampaigns } =
    useEntityFilterSummary({
      customer: '',
      job: '',
      product,
    })
  const availablePlatforms = useMemo(
    () => availablePlatformCounts.map((p) => p.value),
    [availablePlatformCounts],
  )

  // May this product's viewer choose an ingest source at all? The SERVER
  // decides: it pins every fixed department to `sprinklr` and replaces whatever
  // the client sent, so on those pages the toggle was a control that refetched
  // everything and changed nothing. The answer rides on the product card, not
  // on a conditional over product slugs here — this file must never learn WHICH
  // product it is showing (§4).
  const { products, isSettled: catalogueSettled } = useSkyProducts()
  const sourceSelectable = useMemo(
    () => sourceIsSelectable(product, products),
    [product, products],
  )


  // Both date filters open on "up to the end of yesterday", UTC (Kai, 20 Oct
  // 2026): the import runs at 00:05 UTC, so yesterday is the last whole day.
  // Fixed at load, so the page does not move its own range at midnight.
  const [defaultDateRange] = useState(() => defaultSkyDateRange(new Date()))

  const filters = useSocialFilters({
    product,
    availablePlatforms,
    sourceSelectable,
    defaultDateRange,
  })
  const { labelQuery } = filters
  const selectedCount = useMemo(
    // The ingest pin is fixed, never a selection, so it never scores.
    () => countActiveFilters(filters.filterState, { sourceVisible: false, defaultDateRange }),
    [filters.filterState, defaultDateRange],
  )


  // 🔴 NO DATA REQUEST UNTIL THE CATALOGUE HAS ANSWERED (speed review,
  // 16 Oct 2026). Whether this product's source is selectable comes from the
  // catalogue, and it changes `labelQuery` — so on the All products page every
  // panel fired once with the job-wide, both-sources scope (the most expensive
  // shape there is), then again with Sprinklr pinned once the catalogue landed.
  // Waiting for it costs one small, session-cached request; a department's
  // scope does not change when it lands, but this file must not learn which
  // product it is showing, so every page waits the same way. `isSettled`
  // includes a failed catalogue, so the page can never wait for ever.
  const dataReady = catalogueSettled

  const {
    data: chartData,
    isLoading: chartsFetchingFirst,
    isUpdating: chartsUpdating,
    error: chartsError,
  } = useSkyCharts(product, STANDARD_CHARTS, dataReady, labelQuery)
  const {
    metrics,
    totalEntities,
    postsOutsideSprinklrTable,
    isLoading: metricsFetchingFirst,
    isUpdating: metricsUpdating,
  } = useSkyEntityMetrics(product, dataReady, labelQuery)
  // "Loading" means NOTHING TO SHOW YET: before the catalogue answers, or
  // during the first fetch. A filter change keeps the previous numbers on
  // screen (keepWhileOnlyFilterChanges) and is `…Updating` instead, so the
  // controls stay usable and the panels dim rather than blank.
  const chartsLoading = !dataReady || chartsFetchingFirst
  const metricsLoading = !dataReady || metricsFetchingFirst
  const { culturalRelevance, isUpdating: culturalUpdating } = useSkyCulturalRelevance(product, dataReady, labelQuery)
  const outputShare = useSkyOutputOpinionShare(product, outputTagKey, dataReady, labelQuery)
  // Codes a person accepted into the dynamic opinion frame, marked under the
  // opinion charts so a step in a trend that is a new code is never read as a
  // change in what people said (D13).
  const { data: addedOpinionCodes = [] } = useFrameCodeHistory(SKY_SOCIAL_CUSTOMER, SKY_SOCIAL_JOB, 'opinion', dataReady)
  const opinionCodesNote =
    addedOpinionCodes.length > 0 ? (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', px: 3, pb: 1 }}
        data-testid="sky-opinion-codes-added"
      >
        New codes: {describeAddedCodes(addedOpinionCodes)}. Comments before a code was added were not coded with it
        unless they were re-coded.
      </Typography>
    ) : null


  // The comment stream is fetched only once its section is opened: it is a
  // paged record query, and a collapsed section has nothing to show for it.
  const comments = useSkyComments(product, commentsExpanded && dataReady, labelQuery)
  // The HEADLINE count is its own request, because the stream above is
  // deliberately deferred behind the collapsed section — and a not-loaded count
  // rendered identically to a real zero is what put "0 comments" beside "1,180
  // videos" on a job holding 41,307. See useSkyCommentCount.
  const { count: commentCount, isUpdating: commentCountUpdating } = useSkyCommentCount(product, labelQuery, dataReady)

  // How many comments in this scope are NOT in the charts below, and why.
  //
  // Fetched with the page rather than on modal open: the button carries the
  // count and hides itself at zero, so the number must exist before anyone can
  // click. It is a NEW number, not a third copy of commentCount — it does not
  // reopen the decision that removed the header comment tile.
  const { disclosure, notAnalysed, isUpdating: disclosureUpdating } = useSkyUncodedDisclosure(product, labelQuery, dataReady)

  // What each platform button is worth under these filters. Keyed on the same
  // labelQuery as everything else, so the numbers on the buttons move with the
  // page — the first version read them off the unfiltered filter-summary and
  // advertised 57,825 comments over a page showing 16,518.
  const { counts: platformCounts, isUpdating: platformCountsUpdating } = useSkyPlatformCounts(product, labelQuery, dataReady)
  // And the Source row's, the same way (social, influencer, …).
  const { counts: sourceCounts } = useSkySourceCounts(product, labelQuery, dataReady)

  const handleClearAllFilters = useCallback(() => {
    filters.clearLabels()
    filters.clearRangeFilters()
    filters.clearPlatforms()
    filters.clearSources()
    filters.clearMetadataFilters()
    // Back to the page's default ("up to yesterday"), not to unbounded.
    filters.resetDateRanges()
    // 🔴 NO LONGER A NO-OP ON THE DEPARTMENTS (#1199, 2026-09-07). This used to
    // read "the selection is always empty — their campaigns are fixed by the
    // manifest and applied server-side". That was true of the OLD server rule,
    // which discarded whatever campaign list a department's client sent. It now
    // honours a narrowing WITHIN the department's own campaigns — that is the
    // picker in the bar above — so Clear All genuinely has something to clear on
    // Corporate and Product, and the badge counts it (countActiveFilters).
    //
    // The manifest list is still the ceiling and is still applied server-side.
    // What changed is that the user may ask for LESS of it, never more.
    filters.clearCampaigns()
  }, [filters])

  // An account is a property of the POST. A comment whose post was never
  // matched belongs to no account, so the account filter drops it — and the
  // caption has to say so.
  const accountFiltered = (labelQuery?.entity?.labelFilters?.account?.length ?? 0) > 0

  // Row 1 reads the metrics and the comment total; row 2's tiles read the
  // charts, the metrics and cultural relevance. A row dims when any of its
  // sources is showing the previous filter's answer.
  const row1Updating = metricsUpdating || commentCountUpdating
  const row2Updating = chartsUpdating || metricsUpdating || culturalUpdating

  const kpiRow2Context: KpiRow2Context = {
    chartData,
    chartsLoading,
    metrics,
    metricsLoading,
    totalEntities,
    culturalRelevance,
    commentDateRange: filters.commentDateRange,
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', bgcolor: 'background.default' }}>
      {/* The department tab strip spans the WHOLE page, sidebar included, and
          sits outside the scrolling column so it stays put while a dashboard is
          scrolled. It replaced the chooser page (Matt, 2026-08-17), so it is the
          only way to change department from here — which is why it must not be
          able to scroll out of reach. */}
      <ProductTabs product={product} />

      {/* `position: relative` is what the overlay below anchors to. It covers
          the sidebar and the scrolling content but NOT the tab strip above, so
          a department switch stays available while a slow page loads. */}
      <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 0, position: 'relative' }}>
      {/* ⚠ A SELF-CONTAINED LEAF, NOT A HOOK CALL IN THIS COMPONENT. It
          subscribes to the query cache itself, so the cache churn of a load
          re-renders only it — see SkyLoadProgress.tsx. */}
      <SkyLoadProgress />
      {/* The sidebar is the SAME shell the admin dashboard uses; the two differ
          only in what they put inside it. That difference is COMPOSITION — this
          page passes two controls, the admin page passes more — and must never
          become a `variant` prop here (§4 of the plan). */}
      <FilterSidebar
        activeCount={selectedCount}
        onClearAll={handleClearAllFilters}
        disabled={chartsLoading}
      >
        {sidebarExtra?.(filters)}
        <LabelFilterBuilder
          customer=""
          job=""
          product={product}
          selectedLabels={filters.selectedLabels}
          onChange={filters.setSelectedLabels}
          rangeFilters={filters.rangeFilters}
          onRangeFiltersChange={filters.setRangeFilters}
          publishedDateRange={filters.publishedDateRange}
          onPublishedDateRangeChange={filters.setPublishedDateRange}
          /* 🔴 THE COMMENT-DATE PICKER RENDERS ONLY WHEN THIS HANDLER EXISTS
             (#1200). Everything else about the filter was already built and
             working — `useSocialFilters` held the state, `buildLabelQuery`
             emitted it, four store lowerings turned it into `source_date`
             predicates, and `handleClearAllFilters` even cleared it. The one
             missing link was this prop, so DateRangeSection drew the Published
             block and skipped the Comment one, and the state it drove was
             permanently null. Same failure class as the platform bar that lost
             its mount — see alwaysOnFilters.test.ts.

             ⚠ It narrows COMMENTS ONLY. See the Key Metrics caption below. */
          commentDateRange={filters.commentDateRange}
          onCommentDateRangeChange={filters.setCommentDateRange}
          /* The entity labels offered as sidebar facets are CURATED, like the
             record metadata below: `account` only. Source / discipline is not
             offered because no data axis for it exists. */
          labelKeys={SKY_SIDEBAR_LABEL_KEYS}
          disabled={chartsLoading}
        />
        {/* Record metadata is CURATED here, and the curated list is empty
            (Kai, 2026-08-14: "we should specifically choose what record
            metadata filters we add… only channel right now, and we've already
            got that at the top where we click platform"). Discovery put
            `parent_post_id`, `permalink` and `platform_comment_id` in the
            sidebar — unique-per-record ids, so every value has count 1 and
            none of them is a filter. See SKY_SIDEBAR_METADATA_KEYS. This
            renders nothing today; it stays mounted so adding a key back is one
            line there rather than a re-wiring here. */}
        <MetadataFilterSection
          customer=""
          job=""
          product={product}
          selected={filters.metadataFilters}
          onChange={filters.setMetadataFilters}
          disabled={chartsLoading}
          allowKeys={SKY_SIDEBAR_METADATA_KEYS}
        />
      </FilterSidebar>

      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
      <PageHeader
        title={title}
        gradient={BRAND_GRADIENT}
        // The spend page existed with no way to reach it: the route was
        // registered and nothing in the product linked to it, so a department
        // could only see what its analysis cost by typing the URL.
        //
        // (2026-08-24) Gated behind TOKEN_SPEND_VISIBLE — Matt wants no
        // token/spend surface visible to client users before budget
        // conversations happen. Mirrors VOICE_ASSISTANT_ENABLED: default off,
        // one flag, no second entry point left rendering when it's off.
        actions={
          <>
            {/* Post data is no longer reached from here (Kai, 2026-09-10). It is a
                whole-job editor, so a button on every department's dashboard
                handed every campaign to every department. It is the Admin tab
                in ProductTabs now, offered only to admins. */}
            {/* (2026-08-24) Gated behind TOKEN_SPEND_VISIBLE — Matt wants no
                token/spend surface visible to client users before budget
                conversations happen. Mirrors VOICE_ASSISTANT_ENABLED: default
                off, one flag, no second entry point left rendering when it's
                off. */}
            {TOKEN_SPEND_VISIBLE && (
              <Button
                color="info"
                variant="outlined"
                size="small"
                startIcon={<ReceiptLongIcon />}
                data-testid="sky-product-spend-link"
                // Lands on the ALL-products total, not this department — see
                // spendLandingProduct. The page's dropdown filters back down.
                onClick={() =>
                  router.navigate('sky-social-media-product-spend', {
                    product: spendLandingProduct(products, product),
                  })
                }
              >
                Token spend
              </Button>
            )}
          </>
        }
        // 🔴 NO VIDEO/COMMENT COUNTS HERE ANY MORE (Kai, 2026-08-14: "we don't
        // really need the summary of how many videos and comments at the top…
        // just hide that because the platform filter does that").
        //
        // They were a THIRD place the same two numbers appeared — header, Key
        // Metrics caption, and now the All button — and the header's pair was
        // the one with nothing beside it to explain what it counted. Two copies
        // is already one too many; three is how a disagreement goes unnoticed,
        // which is exactly what happened when the buttons were computed
        // unfiltered and nobody could tell which of the numbers was wrong.
        meta={
          subtitle ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          ) : undefined
        }
      />

      {/* PLATFORM / AUTHOR / SOURCE — directly under the header, never in the
          sidebar. All three change every number on the page, and the bar's own
          doc makes the argument: "a default the user cannot see is its own kind
          of lie". The sidebar is for filters you opt into; these are always
          applied, so they are always visible.

          This bar was built for the retired SocialAnalyticsDashboard and lost
          its mount in the sidebar rework — the hook (`useSocialFilters` already
          exposes platformSelection/authorScope/sourceScope) and the query
          builder (`buildLabelQuery` already folds all three into labelQuery)
          never stopped working. Only the render was missing. */}
      <PlatformFilterBar
        platforms={availablePlatformCounts}
        counts={platformCounts}
        countsUpdating={platformCountsUpdating}
        /* CAMPAIGN — the widest cut of all, and the reason it sits here rather
           than in the sidebar (#1199, 2026-09-07). It decides WHICH of Sky's
           content this page is about, where platform decides where that content
           was published; a department's campaign scope is applied whether or not
           the user touches the control, and the bar's rule is that always-applied
           scopes stay visible. The sidebar is for filters you opt into.

           The picture the issue attached is the shape of it: Corporate is four
           campaigns, Product is four more, and Sky could only see them summed.

           ⚠ THE CONTROL DRAWS ITSELF ONLY WHEN THERE IS A CHOICE. Brand,
           Entertainment and Sports own exactly ONE campaign each, so
           PlatformFilterBar renders nothing for them — which is why this file
           can pass the props unconditionally and still never learn which
           department it is showing (§4). */
        campaigns={campaignFilter ? availableCampaigns : undefined}
        selectedCampaigns={filters.campaigns}
        onCampaignsChange={campaignFilter ? filters.setCampaigns : undefined}
        selection={filters.platformSelection}
        onSelectPlatform={filters.selectPlatform}
        /* SOURCE — the second row, the post's business source (Social,
           Influencer; PR and Internal once they exist). An entity filter like
           Platform, so it narrows every panel on the page. */
        sourceChoice={filters.sourceChoice}
        onSelectSource={filters.selectSource}
        sourceCounts={sourceCounts}
        disabled={chartsLoading}
      />

      {chartsError && (
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          {chartsError.message}
        </Alert>
      )}

      {/* 🔴 THE GRID BELOW MIXES TWO POPULATIONS, AND THE CAPTION MUST SAY SO.
          Sky Relevant, Sky Attributed and Positive sentiment are comment
          percentages, while reach, views, engagements and the ER tiles are
          properties of the post that arrive from Sprinklr's own insights
          (auto_coder_entity_metrics).

          The two genuinely answer different questions and both are legitimate,
          which is why this is a caption and not a filter. Making them agree was
          tried and measured (2026-08-14): narrowing the video population to
          videos having a comment that passes the record filter takes Sports
          from 27 videos to 0 and Corporate from 55 to 5 — and every one of
          those videos carries real reach and engagement. A video's reach does
          not become unknown because the comments we ingested came by a
          different path. The filter flows PARENT → CHILD only.

          ⚠ THE COMMENT-DATE FILTER (#1200) IS THE SHARPEST CASE OF THIS, which
          is why the caption is built by `keyMetricsCaption` rather than inline
          here. `source_date` is a column on the RECORD, so
          `/entities/metrics/query` has no commentDateRange field to accept: a
          user who picks February and reads an unqualified "Post metrics from 27
          posts" is being told those posts were narrowed, and they were not. The
          caption says which half moved. The account filter is the mirror case:
          it narrows posts, and comments with no matched post drop out. */}
      <SectionHeader
        title="Key Metrics"
        subtitle={keyMetricsCaption(totalEntities, commentCount, filters.commentDateRange, accountFiltered, postsOutsideSprinklrTable)}
        /* Since the header's count tiles were removed (see the note on the
           PageHeader above), this caption is the ONLY place the video and
           comment counts are on screen — so it is what the browser test has to
           read to check the page's number against the API's for the same
           filter. This is the testid subtitleTestId was added for. */
        subtitleTestId="sky-product-metrics-caption"
        /* 🔴 ANCHORED HERE BECAUSE THE SUBTITLE ABOVE IS THE CLAIM IT QUALIFIES.
           That sentence says "comment metrics from N comments" directly over
           the charts it describes, and N counts only comments we could analyse.
           A reader comparing it with Sky's own dashboard sees a smaller number
           and no reason; this button is the reason, one line away from the
           claim rather than parked in a corner of the page.

           The button owns its own modal and hides itself when there is nothing
           to disclose, so this page places one element and learns nothing about
           dialogs — the arrangement-and-copy contract this file is under. */
        action={<UncodedDisclosureButton notAnalysed={notAnalysed} disclosure={disclosure} updating={disclosureUpdating} />}
      />


      {/* THE CUSTOMER'S SIX-ROW GRID. The rows and their order are Sky's own
          layout; each row carries a testid so the order is pinned by a test. */}
      <Box data-testid="sky-product-grid">
        {/* Each row dims while it shows the previous filter's numbers and the
            new ones load (updatingProps / updatingSx) — nothing blanks on a
            filter change. The dim goes ON the row so the rows stay the grid's
            direct children, in Sky's order. */}
        {/* Row 1: COVERAGE VOLUME | REACH | VIDEO VIEWS | ENGAGEMENTS */}
        <Box
          data-testid="sky-grid-row-1"
          {...updatingProps(row1Updating)}
          sx={{ ...KPI_ROW_SX, ...updatingSx(row1Updating) }}
        >
              <CoverageTile
                totalEntities={totalEntities}
                commentCount={commentCount}
                isLoading={metricsLoading}
              />
              <EntityMetricTiles
                keys={['video_reach', 'video_views', 'engagements']}
                metrics={metrics}
                isLoading={metricsLoading}
              />
        </Box>


        {/* Row 2: the department's choice; see `kpiRow2`. Its tiles read the
            charts, the metrics and cultural relevance, so any of them updating
            dims the row. */}
        <Box
          data-testid="sky-grid-row-2"
          {...updatingProps(row2Updating)}
          sx={{ ...KPI_ROW_SX, ...updatingSx(row2Updating) }}
        >
          {kpiRow2(kpiRow2Context)}
        </Box>

        {/* Row 3: OUTPUT OPINION SHARE | RESPONSE OPINION SHARE */}
        <Box
          data-testid="sky-grid-row-3"
          {...updatingProps(outputShare.isUpdating)}
          sx={{ ...WIDE_ROW_SX, ...updatingSx(outputShare.isUpdating) }}
        >
          <OutputShareCell
            data={outputShare.data}
            totalEntities={outputShare.totalEntities}
            isLoading={outputShare.isLoading}
            error={outputShare.error}
            tagKey={outputTagKey}
            onTagKeyChange={setOutputTagKey}
          />
          {hasRows(chartData[OPINION_SHARE_CHART.id]) ? (
            <Box>
              <ThemeBreakdown definitions={[OPINION_SHARE_CHART]} data={chartData} isLoading={chartsLoading} columns={1} />
              {opinionCodesNote}
            </Box>
          ) : (
            <PlaceholderPanel
              title="Response opinion share"
              message="Needs Sky's list of opinions. We'll code comments against it once it is agreed."
              data-testid="sky-product-placeholder-response-opinion-share"
            />
          )}
        </Box>

        {/* Row 4: sentiment and emotion over time */}
        <Box data-testid="sky-grid-row-4" {...updatingProps(chartsUpdating)} sx={updatingSx(chartsUpdating)}>
          <ThemeBreakdown definitions={ROW4_CHARTS} data={chartData} isLoading={chartsLoading} />
        </Box>

        {/* Row 5: sentiment of the Sky Relevant and Sky Attributed comments over time */}
        <Box data-testid="sky-grid-row-5" {...updatingProps(chartsUpdating)} sx={updatingSx(chartsUpdating)}>
          <ThemeBreakdown definitions={ROW5_CHARTS} data={chartData} isLoading={chartsLoading} />
        </Box>

        {/* Row 6: opinions over time */}
        <Box data-testid="sky-grid-row-6" {...updatingProps(chartsUpdating)} sx={updatingSx(chartsUpdating)}>
          {hasRows(chartData[OPINIONS_OVER_TIME_CHART.id]) ? (
            <>
              <ThemeBreakdown
                definitions={[OPINIONS_OVER_TIME_CHART]}
                data={chartData}
                isLoading={chartsLoading}
                columns={1}
              />
              {opinionCodesNote}
            </>
          ) : (
            <PlaceholderPanel
              title="Opinions over time"
              message="Needs Sky's list of opinions. We'll chart it by month once comments are coded against it."
              height={160}
              data-testid="sky-product-placeholder-opinions-over-time"
            />
          )}
        </Box>
      </Box>

      {/* The breakdowns the grid has no slot for. Their data is already loaded
          for the grid's tiles, so opening this costs no request. */}
      <CollapsibleSection
        title="Breakdowns"
        subtitle="Sentiment, emotion and attribution in full"
        icon={<BarChartIcon />}
        expanded={breakdownsExpanded}
        onToggle={() => setBreakdownsExpanded((v) => !v)}
        data-testid="sky-product-breakdowns"
      >
        <UpdatingFade updating={chartsUpdating}>
        <ThemeBreakdown
          definitions={OVERVIEW_CHARTS}
          data={chartData}
          isLoading={chartsLoading}
          height={TALL_CHART_HEIGHT}
          useGradients={false}
          hideLegend
        />
        <ThemeBreakdown definitions={ATTRIBUTION_CHARTS} data={chartData} isLoading={chartsLoading} />
        <ThemeBreakdown definitions={ATTRIBUTION_TREND_CHARTS} data={chartData} isLoading={chartsLoading} />
        </UpdatingFade>
      </CollapsibleSection>

      <CollapsibleSection
        title="Comments"
        icon={<ChatBubbleOutlineIcon />}
        badge={commentCount}
        expanded={commentsExpanded}
        onToggle={() => setCommentsExpanded((v) => !v)}
        data-testid="sky-product-comments"
      >
        <CommentStream
          comments={comments.comments}
          frameworksMap={comments.frameworksMap}
          totalCount={comments.totalCount}
          page={comments.page}
          rowsPerPage={comments.rowsPerPage}
          onSearchChange={comments.setSearchText}
          onPageChange={comments.setPage}
          onRowsPerPageChange={comments.setRowsPerPage}
        />
      </CollapsibleSection>
      </Box>
      </Box>
    </Box>
  )
}

export default StandardProductDashboard
